import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, ChevronDown, KeyRound } from 'lucide-react';
import { useAuth, useSignOut } from '@kss/auth';
import { useClickOutside } from '../../hooks/useClickOutside';

/**
 * Panel topbar user dropdown. Backed by the real auth slice — was
 * previously rendering a `mockUser` constant + no-op Profile / Settings
 * buttons + sync-dispatched `logout()` (which never revoked the server
 * session).
 *
 * Bug ref: AI Agent — "dashboard user icon not show properly, profile
 * click not view page, setting button not working".
 */
export default function UserProfileDropdown() {
  const { user } = useAuth();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const displayName = user?.name?.trim() || user?.email || 'Account';
  const email = user?.email ?? '';
  const role = user?.role ?? '';
  const initial = (displayName.charAt(0) || 'K').toUpperCase();
  const avatar = user?.avatar;

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      setOpen(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <div ref={ref} className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-surface-card-hover"
      >
        {avatar ? (
          <img
            src={avatar}
            alt=""
            onError={(e) => {
              // Hide broken images so the initial-fallback shows.
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {initial}
          </span>
        )}
        <span className="hidden text-sm font-medium text-content-primary md:block">
          {displayName}
        </span>
        <ChevronDown size={14} className="hidden text-content-muted md:block" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-60 rounded-lg border border-border bg-surface-card shadow-lg"
        >
          {/* Profile header — real user data, with avatar / initial. */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            {avatar ? (
              <img
                src={avatar}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {initial}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-content-primary">
                {displayName}
              </p>
              {email && (
                <p className="truncate text-xs text-content-muted">{email}</p>
              )}
              {role && (
                <span className="mt-1 inline-block rounded-full bg-primary-light px-2 py-0.5 text-[10px] font-medium text-primary">
                  {role}
                </span>
              )}
            </div>
          </div>
          {/* Action items — Profile + Change password + Settings — all
              now navigate to real routes. Settings lands on the
              user-management page (closest equivalent to a dedicated
              settings hub today; replace with `/panel/settings` once
              that page exists). */}
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/panel/profile')}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
            >
              <User size={16} />
              My profile
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/panel/profile#security')}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
            >
              <KeyRound size={16} />
              Change password
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/panel/users')}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-card-hover hover:text-content-primary"
            >
              <Settings size={16} />
              Settings
            </button>
          </div>
          <div className="border-t border-border py-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              disabled={signingOut}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-danger transition-colors hover:bg-danger-light disabled:opacity-60"
            >
              <LogOut size={16} />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
